/**
 * InboxDetailsModal - Detalhes da inbox em modal (substitui a página /tenant/inboxes/:id)
 *
 * Layout replicado do `InstanceDetailsModal` (auditoria premium):
 * - Header com tile do canal, código, status e descrição
 * - Seção "Resumo" (grid chave/valor)
 * - Seção "Movimentações" (timeline com scroll próprio) — inclui conexão,
 *   ativação/desativação e vínculo/desvínculo de instância
 * - Seções "Conexão" e "Testes" apenas quando o provider exige (Meta)
 *
 * RELEASE:
 *   2026-08-08 v2.0.0 - Padronização com o modal de instância + timeline de
 *                       movimentações via `get_inbox_audit_details_safe`.
 *   2026-08-08 v1.0.0 - Versão inicial (migração da página para modal).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Settings,
  ExternalLink,
  KeyRound,
  ClipboardCheck,
  Info,
  History,
  Server,
  Star,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useIsMobile } from '@/hooks/use-mobile';

import { useInboxDetails } from '@/hooks/useInboxDetails';
import { useInboxAuditDetails } from '@/hooks/useInboxAuditDetails';
import { getChannelVisual } from '@/components/tenant/inbox/channelVisuals';
import { formatInboxIdentifierForDisplay } from '@/lib/phone-utils';
import { AuditSummaryRow } from '@/components/tenant/shared/audit/AuditSummaryRow';
import { AuditTimelineItem } from '@/components/tenant/shared/audit/AuditTimelineItem';

import { WabaCredentialsCard } from '@/components/tenant/inbox/WabaCredentialsCard';
import { InstagramCredentialsCard } from '@/components/tenant/inbox/InstagramCredentialsCard';
import { ConnectionStatusCard } from '@/components/tenant/inbox/ConnectionStatusCard';
import { TestMessageCard } from '@/components/tenant/inbox/TestMessageCard';
import { MetaEmbeddedSignupDialog } from '@/components/tenant/inbox/MetaEmbeddedSignupDialog';
import { InstagramSetupGuide } from '@/components/tenant/inbox/InstagramSetupGuide';
import { InstagramReconnectDialog } from '@/components/tenant/inbox/InstagramReconnectDialog';
import { EditInboxDialog } from '@/components/tenant/EditInboxDialog';
import { MetaWabaWebhookStatus } from '@/components/tenant/inbox-details/meta-waba/MetaWabaWebhookStatus';
import { MetaWabaPermissionDiagnostic } from '@/components/tenant/inbox-details/meta-waba/MetaWabaPermissionDiagnostic';

// ============================================================================
// CONFIGURAÇÕES DE STATUS (mesma linguagem visual dos cards de inbox)
// ============================================================================

const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  pending_setup: 'Aguardando ativação',
  active: 'Conectado',
  inactive: 'Inativo',
  connecting: 'Conectando',
  error: 'Erro',
  disconnected: 'Desconectado',
  suspended: 'Suspensa',
};


/**
 * Cor da bolinha indicativa por status (usada no lugar do badge no header).
 * Mantém a mesma linguagem cromática do `statusTone`, porém o texto fica
 * na cor padrão do conteúdo (preta no tema claro).
 */
const statusDot: Record<string, string> = {
  active: 'bg-emerald-500',
  connecting: 'bg-blue-500',
  pending: 'bg-amber-500',
  pending_setup: 'bg-amber-500',
  error: 'bg-destructive',
  disconnected: 'bg-orange-500',
  suspended: 'bg-purple-500',
  inactive: 'bg-muted-foreground',
};


/** Formata data ISO no padrão BR com hora */
function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return '—';
  }
}

// ============================================================================
// PROPS
// ============================================================================

interface InboxDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Id da inbox a ser exibida (pode ser nulo enquanto fechado) */
  inboxId?: string | null;
}

// ============================================================================
// COMPONENTE
// ============================================================================

export function InboxDetailsModal({ open, onOpenChange, inboxId }: InboxDetailsModalProps) {
  const isMobile = useIsMobile();

  // Diálogos empilhados
  const [showReconnectDialog, setShowReconnectDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showInstagramTestDialog, setShowInstagramTestDialog] = useState(false);

  // Aba ativa (só aparece quando há conteúdo de conexão/testes)
  const [activeTab, setActiveTab] = useState<'overview' | 'connection' | 'tests'>('overview');

  // Dados da inbox (só busca quando aberto)
  const {
    data,
    isLoading,
    isLoadingCredentials,
    error,
    refetch,
    checkConnection,
    sendTestMessage,
  } = useInboxDetails(open && inboxId ? inboxId : undefined);

  // Auditoria (resumo + movimentações) — busca somente com o modal aberto
  const { data: audit, isLoading: isLoadingAudit } = useInboxAuditDetails(
    inboxId,
    open,
  );

  const { inbox, credentials, instagramCredentials, isMetaWhatsApp, isMetaInstagram } = data;

  const hasCredentials = !!credentials?.has_access_token || !!instagramCredentials?.has_access_token;
  const isPendingSetup = inbox?.status === 'pending_setup';

  const summary = audit?.summary ?? null;
  const timeline = audit?.timeline ?? [];

  // Abas visíveis conforme o provider
  const showConnectionTab = isMetaWhatsApp || isMetaInstagram;
  const showTestsTab = isMetaWhatsApp || isMetaInstagram;

  // Reseta a aba ao trocar de inbox / reabrir
  useEffect(() => {
    if (open) setActiveTab('overview');
  }, [open, inboxId]);

  const visual = useMemo(() => getChannelVisual(inbox?.channel_type), [inbox?.channel_type]);
  const ChannelIcon = visual.Icon;

  const statusKey = inbox?.status ?? 'inactive';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            'p-0 gap-0 overflow-hidden min-h-0',
            isMobile
              ? 'w-screen h-[100dvh] max-w-none max-h-none rounded-none border-0'
              : 'w-[95vw] max-w-3xl h-[85dvh]',
          )}
        >
          {/* Wrapper interno: cadeia completa de contenção flex */}
          <div className="flex h-full min-h-0 flex-col overflow-hidden">
            {/* ================= Header premium ================= */}
            <div className="shrink-0 px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base flex-wrap">
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-lg',
                      visual.bgColor,
                    )}
                  >
                    <ChannelIcon className={cn('h-3.5 w-3.5', visual.iconColor)} />
                  </div>
                  <span className="truncate">{inbox?.name ?? 'Detalhes da inbox'}</span>
                  {summary?.inbox_code && (
                    <Badge
                      variant="outline"
                      className="font-mono text-[10px] px-1.5 py-0 h-5"
                    >
                      {summary.inbox_code}
                    </Badge>
                  )}
                  {/* Status: bolinha colorida + texto na cor padrão (sem badge) */}
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full shrink-0',
                        statusDot[statusKey] ?? 'bg-muted-foreground',
                      )}
                    />
                    {statusLabels[statusKey] ?? statusKey}
                  </span>

                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Histórico completo para auditoria e rastreamento.
                </DialogDescription>
              </DialogHeader>

              {/* Abas (apenas quando o provider tem conexão/testes) */}
              {inbox && (showConnectionTab || showTestsTab) && (
                <Tabs
                  value={activeTab}
                  onValueChange={(v) => setActiveTab(v as typeof activeTab)}
                  className="mt-3"
                >
                  <TabsList className="h-9 bg-muted/60">
                    <TabsTrigger value="overview" className="text-xs">
                      Visão geral
                    </TabsTrigger>
                    {showConnectionTab && (
                      <TabsTrigger value="connection" className="text-xs">
                        Conexão
                      </TabsTrigger>
                    )}
                    {showTestsTab && (
                      <TabsTrigger value="tests" className="text-xs">
                        Testes
                      </TabsTrigger>
                    )}
                  </TabsList>
                </Tabs>
              )}
            </div>

            {/* ================= Conteúdo ================= */}
            <ScrollArea
              className="flex-1 h-0 min-h-0"
              axis="y"
              touchAxis="pan-y"
              preventScrollChaining
              reserveScrollbarSpace
            >
              <div className="px-6 py-5 pb-8 space-y-6">
                {/* Loading */}
                {isLoading && (
                  <div className="space-y-4">
                    <Skeleton className="h-40 w-full rounded-xl" />
                    <Skeleton className="h-40 w-full rounded-xl" />
                  </div>
                )}

                {/* Erro / não encontrada */}
                {!isLoading && (error || !inbox) && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {error ? 'Erro ao carregar detalhes da inbox' : 'Inbox não encontrada'}
                    </AlertDescription>
                  </Alert>
                )}

                {!isLoading && inbox && (
                  <>
                    {/* Aviso de configuração pendente (Instagram) */}
                    {isMetaInstagram && isPendingSetup && (
                      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                        <div className="flex items-start gap-3">
                          <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                          <div className="space-y-3 text-sm">
                            <div>
                              <p className="font-medium text-foreground">Configuração pendente</p>
                              <p className="mt-0.5 text-muted-foreground">
                                A inbox foi criada, mas ainda não está conectada ao Instagram.
                                Configure o webhook no Meta Developer Portal para concluir.
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() =>
                                  window.open('https://developers.facebook.com/apps/', '_blank')
                                }
                              >
                                <ExternalLink className="h-4 w-4" />
                                Meta Developer Portal
                              </Button>
                              <Button
                                size="sm"
                                className="gap-2"
                                onClick={() => setShowEditDialog(true)}
                              >
                                <KeyRound className="h-4 w-4" />
                                Completar credenciais
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ---------- Aba: Visão geral ---------- */}
                    {activeTab === 'overview' && (
                      <>
                        {/* Seção 1: Resumo */}
                        <section className="space-y-3">
                          <h3 className="text-sm font-semibold flex items-center gap-2">
                            <Info className="h-4 w-4 text-muted-foreground" />
                            Resumo
                          </h3>
                          <div className="rounded-lg border bg-muted/20 p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            <AuditSummaryRow
                              label="Nome"
                              value={
                                <span className="inline-flex items-center gap-1.5">
                                  {inbox.name}
                                  {inbox.is_default && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/20"
                                    >
                                      <Star className="h-3 w-3 mr-0.5 fill-current" />
                                      Padrão
                                    </Badge>
                                  )}
                                </span>
                              }
                            />
                            <AuditSummaryRow label="Canal" value={visual.label} />
                            <AuditSummaryRow
                              label="Provider"
                              value={
                                summary?.provider_display_name ||
                                inbox.provider?.display_name ||
                                inbox.provider?.name ||
                                '—'
                              }
                            />
                            <AuditSummaryRow
                              label="Identificador"
                              mono
                              value={formatInboxIdentifierForDisplay(
                                inbox.channel_type,
                                inbox.identifier,
                              )}
                            />
                            <AuditSummaryRow
                              label="Status"
                              value={statusLabels[statusKey] ?? statusKey}
                            />
                            <AuditSummaryRow
                              label="Grupo"
                              value={summary?.group_name || '—'}
                            />
                            <AuditSummaryRow
                              label="Instância vinculada"
                              value={
                                summary?.instance_name ? (
                                  <span className="flex items-center gap-1.5">
                                    <Server className="h-3 w-3" />
                                    {summary.instance_name}
                                  </span>
                                ) : (
                                  '—'
                                )
                              }
                            />
                            <AuditSummaryRow
                              label="Status da instância"
                              value={
                                summary?.instance_status ? (
                                  <span className="capitalize">
                                    {summary.instance_status.replace(/_/g, ' ')}
                                  </span>
                                ) : (
                                  '—'
                                )
                              }
                            />
                            <AuditSummaryRow
                              label="Criada em"
                              value={formatDate(inbox.created_at)}
                            />
                            <AuditSummaryRow
                              label="Atualizada em"
                              value={formatDate(inbox.updated_at)}
                            />
                          </div>
                        </section>

                        <Separator />

                        {/* Seção 2: Movimentações */}
                        <section className="space-y-3">
                          <h3 className="text-sm font-semibold flex items-center gap-2">
                            <History className="h-4 w-4 text-muted-foreground" />
                            Movimentações
                            <Badge variant="outline" className="text-[10px] ml-auto">
                              {timeline.length}
                            </Badge>
                          </h3>

                          {isLoadingAudit ? (
                            <Skeleton className="h-[200px] w-full rounded-lg" />
                          ) : timeline.length === 0 ? (
                            <p className="text-xs text-muted-foreground italic px-1">
                              Nenhuma movimentação registrada.
                            </p>
                          ) : (
                            // Altura fixa + scroll próprio: evita empurrar as
                            // demais seções para fora da viewport.
                            <ScrollArea
                              className="rounded-lg border bg-muted/20 h-[320px]"
                              axis="y"
                              touchAxis="pan-y"
                              preventScrollChaining
                              reserveScrollbarSpace
                            >
                              <div className="p-4">
                                {timeline.map((event) => (
                                  <AuditTimelineItem key={event.id} event={event} />
                                ))}
                              </div>
                            </ScrollArea>
                          )}
                        </section>

                        {/* Guia de configuração do Instagram */}
                        {isMetaInstagram && isPendingSetup && (
                          <InstagramSetupGuide
                            verifyToken={instagramCredentials?.webhook_verify_token || ''}
                            defaultExpanded
                          />
                        )}
                      </>
                    )}

                    {/* ---------- Aba: Conexão ---------- */}
                    {activeTab === 'connection' && (
                      <div className="grid gap-5 lg:grid-cols-2">
                        {isMetaInstagram && (
                          <div className="lg:col-span-2">
                            <InstagramCredentialsCard
                              credentials={instagramCredentials}
                              inboxId={inbox.id}
                              isLoading={isLoadingCredentials}
                              onCheckConnection={async () => {
                                setShowInstagramTestDialog(true);
                              }}
                              connectionStatus={inbox.status === 'active' ? 'connected' : 'pending'}
                              onEditCredentials={() => setShowEditDialog(true)}
                              showEditButton={
                                isPendingSetup || !instagramCredentials?.has_access_token
                              }
                            />
                          </div>
                        )}

                        {isMetaWhatsApp && (
                          <>
                            <WabaCredentialsCard
                              credentials={credentials}
                              isLoading={isLoadingCredentials}
                              onReconnect={() => setShowReconnectDialog(true)}
                            />
                            <ConnectionStatusCard
                              onCheckConnection={checkConnection}
                              onReconnect={() => setShowReconnectDialog(true)}
                              hasCredentials={hasCredentials}
                            />
                            <MetaWabaWebhookStatus
                              inboxId={inbox.id}
                              hasCredentials={hasCredentials}
                              onStatusChange={refetch}
                            />
                            <MetaWabaPermissionDiagnostic
                              inboxId={inbox.id}
                              hasCredentials={hasCredentials}
                            />
                          </>
                        )}
                      </div>
                    )}

                    {/* ---------- Aba: Testes ---------- */}
                    {activeTab === 'tests' && (
                      <TestMessageCard
                        onSendTestMessage={sendTestMessage}
                        hasCredentials={hasCredentials}
                        disabled={inbox.status !== 'active'}
                        providerSlug={inbox.provider?.slug}
                        inboxId={inbox.id}
                      />
                    )}
                  </>
                )}
              </div>
            </ScrollArea>

            {/* ================= Rodapé ================= */}
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/60 bg-muted/20 px-5 py-3">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                disabled={!inbox}
                onClick={() => setShowEditDialog(true)}
              >
                <Settings className="h-4 w-4" />
                Editar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ================= Diálogos empilhados ================= */}
      {inbox && isMetaWhatsApp && (
        <MetaEmbeddedSignupDialog
          open={showReconnectDialog}
          onOpenChange={setShowReconnectDialog}
          inboxId={inbox.id}
          inboxName={inbox.name}
          onSuccess={() => {
            setShowReconnectDialog(false);
            refetch();
          }}
        />
      )}

      {inbox && (
        <EditInboxDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          inboxId={inbox.id}
          onSuccess={() => {
            setShowEditDialog(false);
            refetch();
          }}
        />
      )}

      {inbox && isMetaInstagram && (
        <InstagramReconnectDialog
          open={showInstagramTestDialog}
          onOpenChange={setShowInstagramTestDialog}
          inboxId={inbox.id}
          inboxName={inbox.name}
          initialStatus={
            inbox.status as
              | 'active'
              | 'inactive'
              | 'pending_setup'
              | 'disconnected'
              | 'error'
              | 'connecting'
          }
          onConnected={() => {
            setShowInstagramTestDialog(false);
            refetch();
          }}
          onEditCredentials={() => setShowEditDialog(true)}
        />
      )}
    </>
  );
}
